import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, CheckCircle2 } from "lucide-react";

export default function TenantSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    tenant_name: "", tenant_slug: "", admin_name: "",
    admin_email: "", admin_password: "", country: "Uganda", phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const slug = (form.tenant_slug || form.tenant_name).toLowerCase()
        .replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
      const { data } = await api.post("/public/tenants/signup", { ...form, tenant_slug: slug });
      setDone(data);
      toast.success("Account created — sign in to get started");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F5F1] p-6">
        <div className="max-w-md text-center bg-white border border-gray-200 rounded-md p-8">
          <CheckCircle2 size={48} className="text-emerald-700 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-gray-900">Welcome to AEOIBCMS</h1>
          <p className="text-gray-600 mt-2 text-sm">
            <strong>{done.tenant?.name}</strong> is now provisioned with a default branch and 5 departments.
            Sign in with the admin email below to start configuring.
          </p>
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-left text-sm">
            <div><span className="text-gray-500">Account slug:</span> <span className="font-mono">{done.tenant?.slug}</span></div>
            <div><span className="text-gray-500">Admin email:</span> <strong>{done.login_email}</strong></div>
          </div>
          <Button onClick={() => navigate("/login")} data-testid="signup-go-login"
            className="mt-6 w-full bg-emerald-900 hover:bg-emerald-950 text-white">
            Go to sign-in →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F4F5F1]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-emerald-950 to-amber-900 text-white">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-amber-500 text-emerald-950 flex items-center justify-center font-display font-extrabold text-xl">A</div>
          <div className="font-display font-bold text-lg">AEOIBCMS</div>
        </div>
        <div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            Run your supermarket like Abrah.
          </h1>
          <p className="mt-4 text-amber-50/90 text-lg">
            13 modules, multi-branch, multi-role, with a public storefront and careers portal — provisioned in seconds.
          </p>
          <ul className="mt-6 space-y-1 text-amber-50/80 text-sm">
            <li>· Issue tracking · Asset registry · Vehicle fleet</li>
            <li>· Suppliers · Customer feedback · Loyalty</li>
            <li>· Online orders · Public storefront · Careers</li>
            <li>· Staff bio data · Approvals · Audit log</li>
            <li>· PDF / Excel / CSV reports across all data</li>
          </ul>
        </div>
        <div className="text-amber-100/60 text-xs">© {new Date().getFullYear()} AEOIBCMS — built for African retail</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 mb-1 font-semibold flex items-center gap-1.5">
            <Sparkles size={12} /> Open your account
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-gray-900">Start in 60 seconds</h2>
          <p className="text-sm text-gray-600 mt-2 mb-6">No credit card. Cancel anytime. Your data is isolated per account.</p>
          <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
            <FieldS label="Supermarket name" required value={form.tenant_name}
              onChange={(v) => setForm({ ...form, tenant_name: v })} testid="signup-tenant-name-input"
              placeholder="e.g. SuperSaver Supermarket Ltd" />
            <FieldS label="Account slug (URL-safe)" value={form.tenant_slug}
              onChange={(v) => setForm({ ...form, tenant_slug: v })} testid="signup-tenant-slug-input"
              placeholder="auto-generated if blank, e.g. supersaver" />
            <div className="grid grid-cols-2 gap-3">
              <FieldS label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
              <FieldS label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>
            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Your admin account</div>
              <FieldS label="Your full name" required value={form.admin_name}
                onChange={(v) => setForm({ ...form, admin_name: v })} testid="signup-admin-name-input" />
              <FieldS label="Email" required type="email" value={form.admin_email}
                onChange={(v) => setForm({ ...form, admin_email: v })} testid="signup-admin-email-input" />
              <FieldS label="Password (min 8 chars)" required type="password" value={form.admin_password}
                onChange={(v) => setForm({ ...form, admin_password: v })} testid="signup-admin-password-input" />
            </div>
            <Button type="submit" disabled={busy}
              className="w-full h-11 bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="signup-submit-button">
              {busy ? "Creating…" : "Create my account"}
            </Button>
            <div className="text-xs text-center text-gray-600 mt-2">
              Already have an account? <Link to="/login" className="text-emerald-900 font-semibold hover:underline">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function FieldS({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">{label}</Label>
      <Input type={type} required={required} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10" data-testid={testid} />
    </div>
  );
}
